import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Rating,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BathtubOutlined as BathtubOutlinedIcon,
  CalendarTodayOutlined as CalendarTodayOutlinedIcon,
  ChevronLeftRounded as ChevronLeftRoundedIcon,
  ChevronRightRounded as ChevronRightRoundedIcon,
  HouseOutlined as HouseOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  KingBedOutlined as KingBedOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  Straighten as StraightenIcon,
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type {
  HomeOwnerMatchingProfile,
  HomeOwnerMatchingProfilePreference,
  BudgetRange,
} from "../../../__generated__/types";
import { useUserContext } from "../../../hooks/userContext";
import { bugetRangeLabels, specialRequirementsLabels } from "../../../utils/option-helpers";
import { SECTION_BORDER } from "../constants";
import { BILLING_METHOD_LABELS } from "../data";
import {
  PROJECT_PROPERTY_TYPE_OPTIONS,
  PROJECT_SCOPE_OPTIONS,
  PROJECT_SPECIAL_REQUIREMENT_OPTIONS,
} from "../../homeowner-bio-page/data";
import { BUDGET_RANGES } from "../../../components/homeowner-preferences-quiz/constants";
import type { HomeownerProjectRecord } from "../types";
import {
  formatBudgetRangeLabel,
  formatCountLabel,
  formatShortDate,
  formatSquareFootageLabel,
  formatValue,
  toTitleCase,
} from "../utils";
import { fetchProjectSummary, type ProjectSummaryResponse } from "../../../services/collaborationApi";
import { normalizeDesignerAliasId } from "../../../utils/designer-alias";

const getOptionLabel = (options: { value: string; label: string }[], value?: string | null) => {
  if (!value) return "";
  const match = options.find((option) => option.value === value || option.label === value);
  return match?.label ?? toTitleCase(value);
};

const getMultiOptionLabel = (
  options: { value: string; label: string }[],
  values?: Array<string | null | undefined>
) => {
  if (!values?.length) return "";
  const labels = values
    .filter(Boolean)
    .map((value) => getOptionLabel(options, String(value)))
    .filter(Boolean);
  return labels.length ? labels.join(", ") : "";
};

const ProjectScopeCell = ({
  label,
  value,
  icon,
  footer,
  footerAlign = "left",
}: {
  label: string;
  value: string;
  icon?: ReactNode;
  footer?: ReactNode;
  footerAlign?: "left" | "right" | "center";
}) => {
  const isPlaceholder = value === "Not provided";
  return (
    <Stack spacing={1}>
      <Typography sx={{ color: "#6B7280", fontSize: "0.95rem", fontWeight: 600 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography
          sx={{
            color: isPlaceholder ? "#9CA3AF" : "#4F46E5",
            fontWeight: 600,
          }}
        >
          {value}
        </Typography>
      </Stack>
      {footer ? (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            justifyContent:
              footerAlign === "right"
                ? "flex-end"
                : footerAlign === "center"
                ? "center"
                : "flex-start",
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Stack>
  );
};

type ProjectTabProps = {
  profile: HomeOwnerMatchingProfile | null;
  preferences: HomeOwnerMatchingProfilePreference | null | undefined;
  projects: HomeownerProjectRecord[];
  homeOwnerId?: string | null;
};

const ProjectTab = ({ profile, preferences, projects, homeOwnerId }: ProjectTabProps) => {
  const { user } = useUserContext();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const projectScrollRef = useRef<HTMLDivElement | null>(null);
  const [projectSummaries, setProjectSummaries] = useState<
    Record<string, ProjectSummaryResponse | null>
  >({});
  const [summaryLoadingId, setSummaryLoadingId] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const summaryUserIds = useMemo(
    () =>
      Array.from(
        new Set([user?.id, homeOwnerId].filter(Boolean) as string[])
      ),
    [homeOwnerId, user?.id]
  );

  const isProjectCompleted = (project: HomeownerProjectRecord) => {
    const normalized = project.status?.trim().toLowerCase().replace(/\s+/g, "_");
    if (normalized) return normalized === "completed";
    return Boolean(project.completionConfirmedAt || project.archivedAt);
  };

  const isProjectActive = (project: HomeownerProjectRecord) => {
    const normalized = project.status?.trim().toLowerCase().replace(/\s+/g, "_");
    if (normalized) {
      return ["active", "in_progress", "paused"].includes(normalized);
    }
    if (isProjectCompleted(project)) return false;
    return Boolean(project.active);
  };

  const sortedProjects = useMemo(() => {
    const list = projects
      .filter(Boolean)
      .filter((project) => isProjectCompleted(project) || isProjectActive(project));
    return [...list].sort((a, b) => {
      const dateA = a.projectStartDate ? new Date(a.projectStartDate).getTime() : 0;
      const dateB = b.projectStartDate ? new Date(b.projectStartDate).getTime() : 0;
      if (dateA !== dateB) return dateB - dateA;
      return (a.projectName ?? "").localeCompare(b.projectName ?? "");
    });
  }, [projects]);

  useEffect(() => {
    if (!sortedProjects.length) {
      setSelectedProjectId(null);
      return;
    }
    if (selectedProjectId && sortedProjects.some((project) => project._id === selectedProjectId)) {
      return;
    }
    const defaultProject =
      sortedProjects.find((project) => project.active) ?? sortedProjects[0];
    setSelectedProjectId(defaultProject?._id ?? null);
  }, [selectedProjectId, sortedProjects]);

  const selectedProject = useMemo(
    () =>
      sortedProjects.find((project) => project._id === selectedProjectId) ??
      sortedProjects[0] ??
      null,
    [selectedProjectId, sortedProjects]
  );

  const selectedProjectSummary = useMemo(() => {
    if (!selectedProject?._id) return null;
    return projectSummaries[selectedProject._id] ?? null;
  }, [projectSummaries, selectedProject?._id]);

  const isCompletedProject = useMemo(() => {
    if (!selectedProject) return false;
    return isProjectCompleted(selectedProject);
  }, [selectedProject]);

  const isSummaryLoading =
    Boolean(selectedProject?._id) && summaryLoadingId === selectedProject?._id;
  const hasSummary =
    Boolean(selectedProject?._id) &&
    Object.prototype.hasOwnProperty.call(projectSummaries, selectedProject?._id ?? "");

  useEffect(() => {
    setSummaryError(null);
  }, [selectedProject?._id]);

  useEffect(() => {
    const projectId = selectedProject?._id;
    const [primaryUserId, fallbackUserId] = summaryUserIds;
    if (!projectId || !primaryUserId) return;
    if (!isCompletedProject) return;
    if (hasSummary) return;
    let cancelled = false;
    setSummaryError(null);
    setSummaryLoadingId(projectId);
    const loadSummary = async () => {
      try {
        const primarySummary = await fetchProjectSummary(projectId, primaryUserId);
        if (
          primarySummary?.feedback ||
          !fallbackUserId ||
          fallbackUserId === primaryUserId
        ) {
          return primarySummary;
        }
        return await fetchProjectSummary(projectId, fallbackUserId);
      } catch {
        if (fallbackUserId && fallbackUserId !== primaryUserId) {
          return await fetchProjectSummary(projectId, fallbackUserId);
        }
        throw new Error("Unable to load project review.");
      }
    };

    loadSummary()
      .then((summary) => {
        if (cancelled) return;
        setProjectSummaries((prev) => ({ ...prev, [projectId]: summary }));
      })
      .catch(() => {
        if (cancelled) return;
        setProjectSummaries((prev) => ({ ...prev, [projectId]: null }));
        setSummaryError("Unable to load project review.");
      })
      .finally(() => {
        if (cancelled) return;
        setSummaryLoadingId((prev) => (prev === projectId ? null : prev));
      });
    return () => {
      cancelled = true;
    };
  }, [hasSummary, isCompletedProject, selectedProject?._id, summaryUserIds]);

  const projectTypeLabel = useMemo(() => {
    const scopeLabel = getMultiOptionLabel(
      PROJECT_SCOPE_OPTIONS,
      selectedProject?.projectScope ?? undefined
    );
    if (scopeLabel) {
      return scopeLabel;
    }
    const projectType = profile?.projectType?.projectType;
    if (Array.isArray(projectType)) {
      const first = projectType.find(Boolean);
      return formatValue(first ? toTitleCase(String(first)) : "", "Not provided");
    }
    return formatValue(
      projectType ? toTitleCase(String(projectType)) : "",
      "Not provided"
    );
  }, [profile?.projectType?.projectType, selectedProject?.projectScope]);

  const projectStartDateLabel = useMemo(
    () => formatShortDate(selectedProject?.projectStartDate),
    [selectedProject?.projectStartDate]
  );

  const budgetRangeLabel = useMemo(() => {
    const budget = selectedProject?.budgetRange ?? preferences?.budgetRange;
    if (!budget) return "Not provided";
    const label =
      BUDGET_RANGES.find((option) => option.value === budget)?.label ??
      bugetRangeLabels[budget as BudgetRange] ??
      toTitleCase(budget);
    return label ? formatBudgetRangeLabel(label) : "Not provided";
  }, [preferences?.budgetRange, selectedProject?.budgetRange]);

  const billingPreferenceLabel = useMemo(() => {
    const methods = (selectedProject?.billingPreferences ??
      preferences?.billingMethods ??
      []) as Array<string | null | undefined>;
    const formatted = methods
      .filter(Boolean)
      .map((method) => BILLING_METHOD_LABELS[method as string] ?? toTitleCase(method));
    return formatted.length ? formatted.join(", ") : "Not provided";
  }, [preferences?.billingMethods, selectedProject?.billingPreferences]);

  const specialRequirementsLabel = useMemo(() => {
    const needs = (selectedProject?.specialRequirements ??
      profile?.projectType?.specialNeeds ??
      []) as Array<string | null | undefined>;
    const formatted = needs
      .filter(Boolean)
      .map((need) => {
        const optionLabel = getOptionLabel(
          PROJECT_SPECIAL_REQUIREMENT_OPTIONS,
          String(need)
        );
        return optionLabel || specialRequirementsLabels[need as string] || toTitleCase(need);
      })
      .filter(Boolean);
    return formatted.length ? formatted.join(", ") : "Not provided";
  }, [profile?.projectType?.specialNeeds, selectedProject?.specialRequirements]);

  const motivationTypeLabel = useMemo(() => {
    const reason = selectedProject?.reasonForHiring ?? "";
    if (!reason) return "Not provided";
    if (
      reason.toLowerCase().startsWith("other") &&
      selectedProject?.otherReasonForHiring?.trim()
    ) {
      return selectedProject.otherReasonForHiring.trim();
    }
    return toTitleCase(reason);
  }, [selectedProject?.otherReasonForHiring, selectedProject?.reasonForHiring]);

  const residenceTimelineLabel = useMemo(() => {
    const value = selectedProject?.ownershipDuration;
    return formatValue(value ? toTitleCase(value) : "", "Not provided");
  }, [selectedProject?.ownershipDuration]);

  const propertyTypeLabel = useMemo(
    () => getOptionLabel(PROJECT_PROPERTY_TYPE_OPTIONS, selectedProject?.propertyType),
    [selectedProject?.propertyType]
  );

  const residenceTypeLabel = useMemo(() => {
    const value = selectedProject?.residenceType;
    return formatValue(value ? toTitleCase(value) : "", "");
  }, [selectedProject?.residenceType]);

  const propertyLocationLabel = useMemo(() => {
    const value = selectedProject?.propertyLocation;
    return formatValue(value ? toTitleCase(value) : "", "");
  }, [selectedProject?.propertyLocation]);

  const squareFootageLabel = useMemo(() => {
    const value = selectedProject?.squareFootage;
    return formatValue(formatSquareFootageLabel(value), "");
  }, [selectedProject?.squareFootage]);

  const bedroomsLabel = useMemo(
    () => formatCountLabel(selectedProject?.bedrooms, "Bed", "Beds"),
    [selectedProject?.bedrooms]
  );

  const bathroomsLabel = useMemo(
    () => formatCountLabel(selectedProject?.bathrooms, "Bath", "Baths"),
    [selectedProject?.bathrooms]
  );

  const getProjectBudgetLabel = (project: HomeownerProjectRecord) => {
    const budget = project.budgetRange ?? preferences?.budgetRange;
    if (!budget) return "Not provided";
    const label =
      BUDGET_RANGES.find((option) => option.value === budget)?.label ??
      bugetRangeLabels[budget as BudgetRange] ??
      toTitleCase(budget);
    return label ? formatBudgetRangeLabel(label) : "Not provided";
  };

  const getProjectScopeLabel = (project: HomeownerProjectRecord) => {
    const scopeLabel = getMultiOptionLabel(
      PROJECT_SCOPE_OPTIONS,
      project.projectScope ?? undefined
    );
    if (scopeLabel) return scopeLabel;
    const projectType = profile?.projectType?.projectType;
    if (Array.isArray(projectType)) {
      const first = projectType.find(Boolean);
      return formatValue(first ? toTitleCase(String(first)) : "", "Not provided");
    }
    return formatValue(projectType ? toTitleCase(String(projectType)) : "", "Not provided");
  };

  const scrollProjects = (direction: "left" | "right") => {
    const container = projectScrollRef.current;
    if (!container) return;
    const cardWidth =
      (container.firstElementChild as HTMLElement | null)?.offsetWidth ?? 240;
    const offset = direction === "left" ? -cardWidth - 16 : cardWidth + 16;
    container.scrollBy({ left: offset, behavior: "smooth" });
  };

  return (
    <Stack
      spacing={{ xs: 4, md: 8 }}
      sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 6, md: "100px" }, px: { xs: 2, md: 0 } }}
    >
      <Typography
        align="center"
        sx={{
          color: "#000",
          fontFamily: "var(--Font-Family-Titles, Jost)",
          fontSize: "var(--Font-Size-2XL, 25px)",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "var(--Font-Line-Height-LG, 40px)",
        }}
      >
        Project scope, logistics, timing, and special needs.
      </Typography>

      <Stack spacing={3} sx={{ px: { xs: 1.5, md: 2 } }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#111827", mb: 2 }}>
            Projects
          </Typography>
          {sortedProjects.length ? (
            <Box sx={{ position: "relative" }}>
              <IconButton
                aria-label="Scroll projects left"
                onClick={() => scrollProjects("left")}
                sx={{
                  position: "absolute",
                  left: -12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                  "&:hover": { backgroundColor: "#F9FAFB" },
                  display: { xs: "none", md: "inline-flex" },
                  zIndex: 1,
                }}
              >
                <ChevronLeftRoundedIcon />
              </IconButton>
              <IconButton
                aria-label="Scroll projects right"
                onClick={() => scrollProjects("right")}
                sx={{
                  position: "absolute",
                  right: -12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                  "&:hover": { backgroundColor: "#F9FAFB" },
                  display: { xs: "none", md: "inline-flex" },
                  zIndex: 1,
                }}
              >
                <ChevronRightRoundedIcon />
              </IconButton>
              <Box
                ref={projectScrollRef}
                sx={{
                  display: "flex",
                  gap: 2,
                  overflowX: "auto",
                  pb: 1,
                  scrollSnapType: "x mandatory",
                  scrollBehavior: "smooth",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {sortedProjects.map((project, index) => {
                  const isSelected =
                    Boolean(project._id) && project._id === selectedProjectId;
                  const startLabel = project.projectStartDate
                    ? `Start ${formatShortDate(project.projectStartDate)}`
                    : "Start date not set";
                  const endDateValue =
                    project.completionConfirmedAt ??
                    project.archivedAt ??
                    project.deadlineDate ??
                    "";
                  const endLabel = endDateValue
                    ? `End ${formatShortDate(endDateValue)}`
                    : "End date not set";
                  const budgetLabel = getProjectBudgetLabel(project);
                  return (
                    <Box
                      key={project._id ?? `${project.projectName ?? "project"}-${index}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (project._id) {
                          setSelectedProjectId(project._id);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          if (project._id) {
                            setSelectedProjectId(project._id);
                          }
                        }
                      }}
                      sx={{
                        minWidth: 220,
                        borderRadius: 2,
                        border: isSelected ? "2px solid #4F46E5" : "1px solid #E5E7EB",
                        backgroundColor: isSelected ? "#EEF2FF" : "#fff",
                        boxShadow: isSelected
                          ? "0 10px 24px rgba(79, 70, 229, 0.18)"
                          : "0 6px 16px rgba(15, 23, 42, 0.06)",
                        p: 2,
                        cursor: project._id ? "pointer" : "default",
                        outline: "none",
                        scrollSnapAlign: "start",
                        "&:focus-visible": {
                          boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.3)",
                        },
                      }}
                    >
                      <Stack spacing={0.6}>
                        <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                          {project.projectName || "Untitled Project"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          {getProjectScopeLabel(project)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                          {startLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                          {endLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          {`Budget ${budgetLabel}`}
                        </Typography>
                        {project.active ? (
                          <Chip
                            label="Active"
                            size="small"
                            sx={{
                              width: "fit-content",
                              backgroundColor: "#ECFDF3",
                              color: "#047857",
                              fontWeight: 600,
                            }}
                          />
                        ) : null}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ color: "#9CA3AF" }}>
              No active or completed projects yet.
            </Typography>
          )}
        </Box>

        {selectedProject ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)",
              },
              gap: 3,
              alignItems: "stretch",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  border: SECTION_BORDER,
                  borderRadius: 2,
                  p: { xs: 2.5, md: 3 },
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    fontSize: "1.1rem",
                  }}
                >
                  Project Scope
                </Typography>
                <Grid container sx={{ mt: 4 }}>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      p: 2,
                      borderRight: { md: "1px solid #E5E7EB" },
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <ProjectScopeCell label="Project Type:" value={projectTypeLabel} />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ p: 2, borderBottom: "1px solid #E5E7EB" }}
                  >
                    <ProjectScopeCell
                      label="Preferred Project Start Date:"
                      value={projectStartDateLabel}
                      icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: "#4F46E5" }} />}
                      footer={
                        selectedProject?.hardDeadline ? (
                          <Chip
                            label={
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Box component="span">Hard Deadline</Box>
                                <Tooltip
                                  arrow
                                  title={
                                    selectedProject?.deadlineDate
                                      ? `Deadline date: ${formatShortDate(
                                          selectedProject.deadlineDate
                                        )}`
                                      : "Deadline date not provided"
                                  }
                                >
                                  <InfoOutlinedIcon
                                    sx={{
                                      fontSize: 14,
                                      color: "#C2410C",
                                      cursor: "help",
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                            }
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "#C2410C",
                              color: "#C2410C",
                              fontWeight: 600,
                              width: "fit-content",
                            }}
                          />
                        ) : null
                      }
                      footerAlign="right"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      p: 2,
                      borderRight: { md: "1px solid #E5E7EB" },
                    }}
                  >
                    <ProjectScopeCell label="Budget Range:" value={budgetRangeLabel} />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ p: 2 }}>
                    <ProjectScopeCell
                      label="Billing Preference:"
                      value={billingPreferenceLabel}
                      footer={
                        selectedProject?.financing ? (
                          <Chip
                            label="Interested in Financing"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "#059669",
                              color: "#059669",
                              fontWeight: 600,
                            }}
                          />
                        ) : null
                      }
                      footerAlign="right"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #E5E7EB" }}>
                  <Typography sx={{ color: "#6B7280", fontWeight: 600 }}>
                    Special Requirements:
                  </Typography>
                  <Typography
                    sx={{
                      mt: 2,
                      color:
                        specialRequirementsLabel === "Not provided"
                          ? "#9CA3AF"
                          : "#4F46E5",
                      fontWeight: 600,
                    }}
                  >
                    {specialRequirementsLabel}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  border: SECTION_BORDER,
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: { xs: 2.5, md: 3 },
                  height: "100%",
                  width: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                      Motivation Type:
                    </Typography>
                    <Typography
                      sx={{
                        mt: 2,
                        color:
                          motivationTypeLabel === "Not provided"
                            ? "#9CA3AF"
                            : "#4F46E5",
                        fontWeight: 600,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {motivationTypeLabel}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                      Residence Timeline
                    </Typography>
                    <Typography
                      sx={{
                        mt: 2,
                        color:
                          residenceTimelineLabel === "Not provided"
                            ? "#9CA3AF"
                            : "#4F46E5",
                        fontWeight: 600,
                      }}
                    >
                      {residenceTimelineLabel}
                    </Typography>
                  </Box>
                  <Box sx={{ borderTop: "1px solid #E5E7EB", pt: 3 }}>
                    <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                      Property Details
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 2 }} alignItems="flex-start">
                      {propertyTypeLabel ? (
                        <Chip
                          icon={<HouseOutlinedIcon sx={{ fontSize: 16 }} />}
                          label={propertyTypeLabel}
                          size="small"
                          sx={{
                            backgroundColor: "#F3F4F6",
                            color: "#374151",
                            fontWeight: 600,
                            justifyContent: "flex-start",
                            borderRadius: "999px",
                            border: "1px solid #E5E7EB",
                            width: "fit-content",
                          }}
                        />
                      ) : null}
                      {residenceTypeLabel ? (
                        <Chip
                          icon={<HouseOutlinedIcon sx={{ fontSize: 16 }} />}
                          label={residenceTypeLabel}
                          size="small"
                          sx={{
                            backgroundColor: "#F3F4F6",
                            color: "#374151",
                            fontWeight: 600,
                            justifyContent: "flex-start",
                            borderRadius: "999px",
                            border: "1px solid #E5E7EB",
                            width: "fit-content",
                          }}
                        />
                      ) : null}
                      {propertyLocationLabel ? (
                        <Chip
                          icon={<LocationOnOutlinedIcon sx={{ fontSize: 16 }} />}
                          label={propertyLocationLabel}
                          size="small"
                          sx={{
                            backgroundColor: "#F3F4F6",
                            color: "#374151",
                            fontWeight: 600,
                            justifyContent: "flex-start",
                            borderRadius: "999px",
                            border: "1px solid #E5E7EB",
                            width: "fit-content",
                          }}
                        />
                      ) : null}
                      {squareFootageLabel ? (
                        <Chip
                          icon={<StraightenIcon sx={{ fontSize: 16 }} />}
                          label={squareFootageLabel}
                          size="small"
                          sx={{
                            backgroundColor: "#F3F4F6",
                            color: "#374151",
                            fontWeight: 600,
                            justifyContent: "flex-start",
                            borderRadius: "999px",
                            border: "1px solid #E5E7EB",
                            width: "fit-content",
                          }}
                        />
                      ) : null}
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {bedroomsLabel ? (
                          <Chip
                            icon={<KingBedOutlinedIcon sx={{ fontSize: 16 }} />}
                            label={bedroomsLabel}
                            size="small"
                            sx={{
                              backgroundColor: "#F3F4F6",
                              color: "#374151",
                              fontWeight: 600,
                              justifyContent: "flex-start",
                              borderRadius: "999px",
                              border: "1px solid #E5E7EB",
                              width: "fit-content",
                            }}
                          />
                        ) : null}
                        {bathroomsLabel ? (
                          <Chip
                            icon={<BathtubOutlinedIcon sx={{ fontSize: 16 }} />}
                            label={bathroomsLabel}
                            size="small"
                            sx={{
                              backgroundColor: "#F3F4F6",
                              color: "#374151",
                              fontWeight: 600,
                              justifyContent: "flex-start",
                              borderRadius: "999px",
                              border: "1px solid #E5E7EB",
                              width: "fit-content",
                            }}
                          />
                        ) : null}
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  border: SECTION_BORDER,
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: { xs: 2.5, md: 3 },
                  height: "100%",
                  width: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                      Review
                    </Typography>
                    {!isCompletedProject ? (
                      <Typography sx={{ mt: 2, color: "#9CA3AF" }}>
                        Review available once the project is completed.
                      </Typography>
                    ) : isSummaryLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                        <CircularProgress size={18} sx={{ color: "#4F46E5" }} />
                        <Typography sx={{ color: "#6B7280" }}>
                          Loading review...
                        </Typography>
                      </Stack>
                    ) : selectedProjectSummary?.feedback ? (
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Rating
                          value={selectedProjectSummary.feedback.rating ?? 0}
                          precision={0.5}
                          readOnly
                        />
                        {selectedProjectSummary.feedback.review ? (
                          <Typography sx={{ color: "#4B5563" }}>
                            {selectedProjectSummary.feedback.review}
                          </Typography>
                        ) : (
                          <Typography sx={{ color: "#9CA3AF" }}>
                            No written review provided.
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography sx={{ mt: 2, color: "#9CA3AF" }}>
                        No review submitted yet.
                      </Typography>
                    )}
                    {summaryError && isCompletedProject ? (
                      <Typography sx={{ mt: 1, color: "#DC2626" }}>
                        {summaryError}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box sx={{ borderTop: "1px solid #E5E7EB", pt: 3 }}>
                    <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                      Designer
                    </Typography>
                    {isSummaryLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                        <CircularProgress size={18} sx={{ color: "#4F46E5" }} />
                        <Typography sx={{ color: "#6B7280" }}>
                          Loading designer...
                        </Typography>
                      </Stack>
                    ) : selectedProjectSummary?.designer ? (
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                        <Avatar
                          src={selectedProjectSummary.designer.profilePicUrl ?? undefined}
                          alt="Designer"
                          sx={{ width: 44, height: 44, bgcolor: "#E5E7EB", color: "#4B5563" }}
                        >
                          D
                        </Avatar>
                        <Stack spacing={0.5}>
                          <Typography sx={{ fontWeight: 600, color: "#111827" }}>
                            Designer
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6B7280" }}>
                            {`Designer ID: ${
                              normalizeDesignerAliasId(selectedProjectSummary.designer.aliasId) ||
                              selectedProjectSummary.designer.id ||
                              "Not provided"
                            }`}
                          </Typography>
                        </Stack>
                      </Stack>
                    ) : (
                      <Typography sx={{ mt: 2, color: "#9CA3AF" }}>
                        Designer not assigned.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              border: SECTION_BORDER,
              borderRadius: 2,
              p: { xs: 3, md: 4 },
              backgroundColor: "#fff",
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>
              Select a project to view details.
            </Typography>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default ProjectTab;
